import React, { createContext, useContext, useState, useEffect } from "react";
import { register as apiRegister, login as apiLogin, logout as apiLogout, getCurrentUser } from "../services/authService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedUser = getCurrentUser();
        if (savedUser) {
          setUser(savedUser);
          console.log('✅ Utilisateur restauré:', savedUser.email);
        }
      } catch (error) {
        console.error('❌ Erreur restauration utilisateur:', error);
        setError('Erreur lors du chargement de la session');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Inscription
 // AuthContext.jsx - version avec debug complet
const register = async (userData) => {
  setLoading(true);
  setError(null);

  try {
    console.log('🔄 AuthContext.register DÉBUT', userData);
    const result = await apiRegister(userData);
    
    console.log('📦 AuthContext.register RÉSULTAT:', result);
    console.log('🔍 STRUCTURE résultat:', {
      hasResult: !!result,
      hasSuccess: result?.success,
      hasUser: result?.user,
      userEmail: result?.user?.email
    });

    // Vérification complète
    if (result && result.success) {
      if (result.user && result.user.email) {
        // Cas normal
        setUser(result.user);
        console.log('✅ AuthContext - Inscription réussie:', result.user.email);
        return { success: true, user: result.user };
      } else {
        // User manquant ou incomplet
        console.warn('⚠️ AuthContext - User incomplet, création manuelle');
        const fallbackUser = {
          id: result.user?.id || Date.now(),
          name: userData.name,
          email: userData.email,
          role: 'client'
        };
        setUser(fallbackUser);
        localStorage.setItem('user', JSON.stringify(fallbackUser));
        console.log('✅ AuthContext - Inscription réussie (fallback):', fallbackUser.email);
        return { success: true, user: fallbackUser };
      }
    } else {
      // Échec
      const errorMsg = result?.error || 'Erreur inconnue lors de l\'inscription';
      console.error('❌ AuthContext - Erreur inscription:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

  } catch (error) {
    console.error('❌ AuthContext - Exception inscription:', error);
    const errorMessage = error.message || 'Erreur lors de l\'inscription';
    setError(errorMessage);
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
};

  // Connexion
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Début connexion...');
      const result = await apiLogin({ email, password });

      if (result.success) {
        setUser(result.user);
        console.log('✅ Connexion réussie:', result.user.email);
        return { success: true, user: result.user };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = error.message || 'Erreur lors de la connexion';
      setError(errorMessage);
      console.error('❌ Erreur connexion:', error);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const logout = () => {
    console.log('🚪 Déconnexion en cours...');
    apiLogout();
    setUser(null);
    setError(null);
    console.log('✅ Déconnexion réussie');
  };

  // Effacer les erreurs
  const clearError = () => {
    setError(null);
  };

  // Valeur du contexte
  const value = {
    // Données
    user,
    loading,
    error,
    
    // Méthodes
    register,
    login,
    logout,
    clearError,
    
    // États dérivés
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé dans un AuthProvider");
  }
  return context;
};